import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import RobitoLogo from "../components/RobitoLogo";
import { apiPostPublic, apiPostAuth } from "../utils/api";
import "../App.css";

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const referralFromUrl = searchParams.get("ref") || ""; // Get referral code from URL

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    company: "",
    country: "",
    referralCode: referralFromUrl, // Prefill with URL referral code if available
    agree: false,
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const checkLogin = async () => {
      try {
        const response = await apiPostAuth("account/is_logged_in.php");
        if (response.status === "success") {
          window.location.href = "/dashboard";
        }
      } catch (error) {
        
      }
    };

    checkLogin();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });

    // Clear error for the changed field
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    const referralRegex = /^[a-zA-Z0-9]*$/;

    if (!form.name.trim()) newErrors.name = "Full name is required.";
    if (!form.email.trim()) newErrors.email = "Email is required.";
    if (!form.password) {
      newErrors.password = "Password is required.";
    } else if (!passwordRegex.test(form.password)) {
      newErrors.password =
        "Password must be at least 8 characters, include uppercase, lowercase, and a number.";
    }

    if (!form.confirmPassword) {
      newErrors.confirmPassword = "Confirm your password.";
    } else if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
    }

    if (!form.company.trim()) newErrors.company = "Company name is required.";
    if (!form.country) newErrors.country = "Please select your country.";
    if (!form.agree)
      newErrors.agree =
        "You must agree to the Terms of Service and Privacy Policy.";

    if (form.referralCode && !referralRegex.test(form.referralCode)) {
      newErrors.referralCode = "Referral code must be alphanumeric only.";
    }

    return newErrors;
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      // Make API call to register.php
      const response = await apiPostPublic("register.php", {
        full_name: form.name,
        email: form.email,
        password: form.password,
        company_name: form.company,
        country: form.country,
        referral_code: form.referralCode || "", // Send empty string if no referral code
      });

      if (response.result === 1) {
        alert("Account created successfully!");
        window.location.href = "/dashboard";
      } else {
        alert(response.message || "Registration failed. Please try again.");
      }
    } catch (error) {
      alert("An error occurred while registering.");
      
    } finally {
      setLoading(false);
    }
  };

  const isFormIncomplete = () => {
    return (
      !form.name.trim() ||
      !form.email.trim() ||
      !form.password ||
      !form.confirmPassword ||
      form.password !== form.confirmPassword ||
      !form.company.trim() ||
      !form.country ||
      !form.agree
    );
  };

  return (
    <div className="page-wrapper">
      <div className="auth-card">
        <RobitoLogo />
        <h2 className="form-title">Create Account</h2>
        <form onSubmit={handleRegister} className="form-container">
          {/* Full Name */}
          <div className="form-group">
            <input
              type="text"
              name="name"
              placeholder="Full Name"
              className="input-field"
              value={form.name}
              onChange={handleChange}
            />
            {errors.name && <span className="error-label">{errors.name}</span>}
          </div>

          {/* Email */}
          <div className="form-group">
            <input
              type="email"
              name="email"
              placeholder="Email"
              className="input-field"
              value={form.email}
              onChange={handleChange}
            />
            {errors.email && <span className="error-label">{errors.email}</span>}
          </div>

          {/* Password */}
          <div className="form-group password-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Password"
              className="input-field"
              value={form.password}
              onChange={handleChange}
            />
            <button
              type="button"
              className="toggle-password"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
            {errors.password && (
              <span className="error-label">{errors.password}</span>
            )}
          </div>

          {/* Confirm Password */}
          <div className="form-group">
            <input
              type={showPassword ? "text" : "password"}
              name="confirmPassword"
              placeholder="Confirm Password"
              className="input-field"
              value={form.confirmPassword}
              onChange={handleChange}
            />
            {errors.confirmPassword && (
              <span className="error-label">{errors.confirmPassword}</span>
            )}
          </div>

          {/* Company Name */}
          <div className="form-group">
            <input
              type="text"
              name="company"
              placeholder="Company Name"
              className="input-field"
              value={form.company}
              onChange={handleChange}
            />
            {errors.company && (
              <span className="error-label">{errors.company}</span>
            )}
          </div>

          {/* Country */}
          <div className="form-group">
            <select
              name="country"
              className="select-field"
              value={form.country}
              onChange={handleChange}
            >
              <option value="" disabled hidden>
                Select Country
              </option>
              <option value="US">United States</option>
              <option value="ID">Indonesia</option>
              <option value="CN">China</option>
              <option value="JP">Japan</option>
              <option value="DE">Germany</option>
              <option value="RU">Russia</option>
              <option value="GB">United Kingdom</option>
              <option value="FR">France</option>
              <option value="CA">Canada</option>
              <option value="KR">South Korea</option>
              <option value="BR">Brazil</option>
              <option value="OTHER">Other Country</option>
            </select>
            {errors.country && (
              <span className="error-label">{errors.country}</span>
            )}
          </div>

          {/* Referral Code (Optional) */}
          <div className="form-group">
            <input
              type="text"
              name="referralCode"
              placeholder="Referral Code (Optional)"
              className="input-field"
              value={form.referralCode}
              onChange={handleChange}
              readOnly={!!searchParams.get("ref")} // Optional: lock if from URL
            />
            {errors.referralCode && <span className="error-label">{errors.referralCode}</span>}
          </div>

          {/* Terms and Conditions */}
          <label className="checkbox-container">
            <input
              type="checkbox"
              name="agree"
              checked={form.agree}
              onChange={handleChange}
            />
            <span>
              By creating an account, you agree to our{" "}
              <Link to="/terms-of-service" className="link">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy-policy" className="link">
                Privacy Policy
              </Link>.
            </span>
          </label>
          {errors.agree && <span className="error-label">{errors.agree}</span>}

          {/* Register Button */}
          <button
            type="submit"
            className="primary-button"
            style={{ opacity: isFormIncomplete() ? 0.6 : 1 }}
            disabled={loading}
          >
            {loading ? "Registering..." : "Register"}
          </button>
        </form>

        <p className="form-footer">
          Already have an account?{" "}
          <Link to="/" className="link">
            Login
          </Link>
        </p>
        <div className="copyright">© Robito.Ai 2025</div>
      </div>
    </div>
  );
};

export default RegisterPage;
